import { buildUniqueSelector } from './selector-utils';

export class AriaLabelledbyIntegrityRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'ariaLabelledbyIntegrity';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode, pageUrl = '') {
		const nodes = this.getCandidateNodes(documentNode);
		const elements = nodes.map((node, index) => this.buildElementInfo(node, index, pageUrl));
		const issues = elements.filter((element) => element.issue.length > 0);

		if (elements.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No aria-labelledby usage found.', 'a11yfix' ),
				details: {
					elements,
					issues
				}
			};
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'aria-labelledby integrity issues found:', 'a11yfix' )} ${issues.length}`,
				details: {
					elements,
					issues
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'All aria-labelledby references resolve to readable text.', 'a11yfix' ),
			details: {
				elements,
				issues
			}
		};
	}

	getCandidateNodes(documentNode) {
		return Array.from(documentNode.querySelectorAll('[aria-labelledby]')).filter((node) => !node.closest('#wpadminbar'));
	}

	buildElementInfo(node, index, pageUrl) {
		const ariaLabelledBy = String(node.getAttribute('aria-labelledby') || '').trim();
		const labelledByIds = ariaLabelledBy.split(/\s+/).filter(Boolean);
		const missingIds = [];
		const hiddenReferenceIds = [];
		const textParts = [];

		for (const labelId of labelledByIds) {
			const labelNode = node.ownerDocument.getElementById(labelId);

			if (!labelNode) {
				missingIds.push(labelId);
				continue;
			}

			if (this.isNodeHiddenFromAssistiveTech(labelNode)) {
				hiddenReferenceIds.push(labelId);
			}

			const text = String(labelNode.textContent || '').trim();
			if (text.length > 0) {
				textParts.push(text);
			}
		}

		const resolvedText = textParts.join(' ').trim();
		let issue = '';

		if (labelledByIds.length === 0) {
			issue = this.__( 'aria-labelledby is present but does not contain valid IDs.', 'a11yfix' );
		} else if (missingIds.length > 0) {
			issue = `${this.__( 'aria-labelledby references missing IDs:', 'a11yfix' )} ${missingIds.join(', ')}`;
		} else if (resolvedText.length === 0) {
			issue = this.__( 'aria-labelledby references do not provide readable text.', 'a11yfix' );
		} else if (hiddenReferenceIds.length > 0) {
			issue = `${this.__( 'aria-labelledby references hidden nodes:', 'a11yfix' )} ${hiddenReferenceIds.join(', ')}`;
		}

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(node),
			tag: node.tagName.toLowerCase(),
			id: node.getAttribute('id') || '',
			name: node.getAttribute('name') || '',
			role: node.getAttribute('role') || '',
			ariaLabelledby: ariaLabelledBy,
			labelledByIds,
			missingIds,
			hiddenReferenceIds,
			resolvedText,
			issue
		};
	}

	isNodeHiddenFromAssistiveTech(node) {
		if (!node || node.nodeType !== 1) {
			return false;
		}

		if (String(node.getAttribute('aria-hidden') || '').toLowerCase() === 'true') {
			return true;
		}

		if (node.hidden) {
			return true;
		}

		return false;
	}
}
